import React, { useEffect, useState } from 'react';
import { VCardService } from '../services/vcardService';
import { useContent } from '../hooks/useContent';
import { useTheme } from '../contexts/ThemeContext';

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 rounded-2xl p-6 md:p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

export default function VCardEditModal({ open, onClose, vcard, onSaved }) {
  const { isDark } = useTheme();
  const { content } = useContent('vCardEditModal');

  /* état initial = toutes les clés de la VCard */
  const [form, setForm] = useState({
    name: '',
    phone: '',
    mail: '',
    company: '',
    first_name: '',
    last_name: '',
    phone_work: '',
    phone_mobile: '',
    website: '',
    job_title: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    personal_note: '',
    birthday: ''
  });

  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (vcard) {
      setForm(vcard.data);          // données snake_case reçues
      setEditingId(vcard.uuid);
    } else {
      setForm({
        name: '',
        phone: '',
        mail: '',
        company: '',
        first_name: '',
        last_name: '',
        phone_work: '',
        phone_mobile: '',
        website: '',
        job_title: '',
        street: '',
        city: '',
        state: '',
        zipcode: '',
        country: '',
        personal_note: '',
        birthday: ''
      });
      setEditingId(null);
    }
  }, [vcard]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    try {
      if (editingId) await VCardService.update(editingId, form);
      else await VCardService.create(form);
      onSaved();
      onClose();
    } catch (e) {
      alert(e?.message || content?.saveError || 'Erreur lors de la sauvegarde');
    }
  };

  if (!content) {
    return (
      <Modal open={open} onClose={onClose}>
        <div className={`rounded-2xl border p-6 md:p-8 space-y-4 ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200'}`}>
          <div className="animate-pulse space-y-3">
            <div className={`h-6 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-1">
                <div className={`h-4 w-20 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
                <div className={`h-10 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
              </div>
            ))}
            <div className="flex justify-end gap-3 pt-4">
              <div className={`h-10 w-20 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
              <div className={`h-10 w-24 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <div className={`rounded-2xl border p-6 md:p-8 space-y-4 ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200'}`}>
        <h2 className="text-2xl font-semibold">{editingId ? content.titleEdit : content.titleCreate}</h2>

        {/* Name */}
        <label className="block">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.name}</span>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
          />
        </label>

        {/* First & Last name */}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.firstName ?? 'Prénom'}</span>
            <input
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.lastName ?? 'Nom'}</span>
            <input
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
        </div>

        {/* Phones */}
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.phone ?? 'Téléphone'}</span>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.phoneWork ?? 'Tél. pro'}</span>
            <input
              name="phone_work"
              value={form.phone_work}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.phoneMobile ?? 'Mobile'}</span>
            <input
              name="phone_mobile"
              value={form.phone_mobile}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
        </div>

        {/* Email */}
        <label className="block">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.mail ?? 'E-mail'}</span>
          <input
            name="mail"
            type="email"
            value={form.mail}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
          />
        </label>

        {/* Website */}
        <label className="block">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.website ?? 'Site web'}</span>
          <input
            name="website"
            type="url"
            value={form.website}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
          />
        </label>

        {/* Company & Job title */}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.company ?? 'Entreprise'}</span>
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.jobTitle ?? 'Poste'}</span>
            <input
              name="job_title"
              value={form.job_title}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
        </div>

        {/* Address */}
        <label className="block">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.street ?? 'Rue'}</span>
          <input
            name="street"
            value={form.street}
            onChange={handleChange}
            className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
          />
        </label>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.city ?? 'Ville'}</span>
            <input
              name="city"
              value={form.city}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.state ?? 'État/Région'}</span>
            <input
              name="state"
              value={form.state}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.zipcode ?? 'Code postal'}</span>
            <input
              name="zipcode"
              value={form.zipcode}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.country ?? 'Pays'}</span>
            <input
              name="country"
              value={form.country}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
          <label className="block">
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.birthday ?? 'Anniversaire'}</span>
            <input
              name="birthday"
              type="date"
              value={form.birthday}
              onChange={handleChange}
              className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
            />
          </label>
        </div>

        {/* Note */}
        <label className="block">
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{content.personalNote ?? 'Note'}</span>
          <textarea
            name="personal_note"
            value={form.personal_note}
            onChange={handleChange}
            rows={3}
            className={`mt-1 w-full rounded-lg border px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'border-dark-600 text-gray-100' : 'border-gray-300 text-gray-900'}`}
          />
        </label>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
          >
            {content.cancel}
          </button>
          <button
            onClick={save}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600"
          >
            {content.save}
          </button>
        </div>
      </div>
    </Modal>
  );
}
